/**
 * CardReissueDialog — account-scoped Debit card re-issue.
 *
 * Fires from: Products tab row action on a DDA/SAV row with a linked card.
 *
 * Flow:
 *   - Capture reason (Lost/Stolen force blockExisting=true + disable the
 *     opt-out checkbox).
 *   - MailTo + card design selection.
 *   - On save → useCardReissue cascade (block old, add new, audit, task).
 */

import { useEffect, useMemo, useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { useApi } from '../../hooks/useApi';
import { useCardReissue, type CardReissueReason } from './useCardReissue';
import type { AccountScopedMaintProps } from '../types';

export interface CardReissueDialogProps extends AccountScopedMaintProps {
  /** Existing card last-4 or full number from the Products tab row. */
  existingCardNumber?: string;
  /**
   * The card's CURRENT design (EFTCardStyleId) from Core, when known — so the
   * picker pre-selects the customer's existing design and reprint keeps it
   * (BACKLOG-0873). Falls back to the library default when absent.
   */
  currentCardStyleId?: string;
}

/** A real, admin-maintained card design (BACKLOG-0873) resolved from
 *  /v3/config/card-types — replaces the former hallucinated PCB_STD/BLUE/MS
 *  list. code = EFTCardStyleId (101/103/104/105…). */
interface ReissueCardDesign {
  code: string;
  label: string;
  imageUrl?: string;
  /**
   * Retired design (IsActive=0) that customers still HOLD — e.g. 102, the Black card
   * (Collette 2026-07-16: "102 was used for the Black card that we no longer offer").
   * Rendered so the FSR can see what the customer has, never selectable.
   */
  discontinued?: boolean;
}

// 101 = Blue Card. (The old comment here said "Rt 66", which was my unsourced guess;
// Collette's Core-authoritative mapping is 101=Blue Card, 103=Dog, 104=Rt 66,
// 105=White Hex — see seed 140.) This default only applies when Core doesn't tell us
// the customer's current design; a reissue normally pre-selects what they already have.
const DEFAULT_DESIGN_CODE = '101';

const REASONS: ReadonlyArray<{ code: CardReissueReason; label: string; forcesBlock: boolean }> = [
  { code: 'Lost',       label: 'Lost',          forcesBlock: true  },
  { code: 'Stolen',     label: 'Stolen',        forcesBlock: true  },
  { code: 'Damaged',    label: 'Damaged',       forcesBlock: false },
  { code: 'Upgrade',    label: 'Design upgrade', forcesBlock: false },
  { code: 'NameChange', label: 'Name change',   forcesBlock: true  },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-100)',
  borderRadius: 'var(--r-sm)',
  fontSize: 13,
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-600)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  fontWeight: 600,
  marginBottom: 4,
  display: 'block',
};

export function CardReissueDialog(props: CardReissueDialogProps): JSX.Element | null {
  const {
    open, onClose, onSaved, cifId, acctId, acctType, prodDesc,
    officerCode = '000', readOnly = false, existingCardNumber, currentCardStyleId,
  } = props;

  const { run } = useCardReissue();
  const { config } = useConfig();
  const { client } = useApi();

  // Read-back (BACKLOG-0873) — resolve the card's CURRENT design from Core on
  // open (unless the caller already supplied it), so the picker pre-selects it.
  const [fetchedStyleId, setFetchedStyleId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!open || currentCardStyleId || !cifId || !acctId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await client.accounts.cardStyle(acctId, cifId);
        if (!cancelled && res.found && res.styleId) setFetchedStyleId(res.styleId);
      } catch {
        /* non-fatal — falls back to the library default */
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentCardStyleId, cifId, acctId, client]);

  const effectiveCurrentStyle = currentCardStyleId ?? fetchedStyleId;

  // Real card-design library from config (BACKLOG-0873): active rows that
  // carry uploaded artwork are the visual designs (101/103/104/105…). If the
  // route hasn't surfaced any yet, fall back to the single default code so the
  // control is never empty.
  const designs = useMemo<ReadonlyArray<ReissueCardDesign>>(() => {
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
    const rows = (config.cardTypes ?? []) as unknown as ReadonlyArray<Record<string, unknown>>;
    const out: ReissueCardDesign[] = [];
    for (const r of rows) {
      const code = String(r['code'] ?? '');
      if (!code) continue;
      const inactive = r['isActive'] === false;
      const imagePath = typeof r['imagePath'] === 'string' ? (r['imagePath'] as string) : undefined;
      const hasImage = r['hasImage'] === true || !!imagePath;

      // A retired design is surfaced ONLY when it is the design this customer actually
      // holds. Otherwise the picker fills with cards nobody can order.
      //
      // Bill 2026-07-16 — before this, an inactive/art-less row was dropped twice over,
      // and initialDesign then fell through to DEFAULT_DESIGN_CODE. So a customer with a
      // Black card (102) asking to replace it got the DEFAULT design pre-selected, the
      // "Pre-selected the customer's current design" hint printed underneath, and a
      // different card in the mail. Silent, and the FSR was actively misinformed.
      const isCurrent = !!effectiveCurrentStyle && code === effectiveCurrentStyle;
      if (inactive && !isCurrent) continue;

      // Designs carry artwork; card PROGRAMS (codes 9/11) don't and must stay out. The
      // customer's own current design is exempt: 102's original art is lost, so it
      // legitimately has none and renders the Discontinued placeholder instead.
      if (!hasImage && !isCurrent) continue;

      const label = (typeof r['description'] === 'string' && r['description']) ? (r['description'] as string) : `Design ${code}`;
      out.push({
        code,
        label,
        ...(imagePath ? { imageUrl: `${apiBase}${imagePath}` } : {}),
        ...(inactive ? { discontinued: true } : {}),
      });
    }
    return out.length > 0 ? out : [{ code: DEFAULT_DESIGN_CODE, label: `Design ${DEFAULT_DESIGN_CODE}` }];
  }, [config.cardTypes, effectiveCurrentStyle]);

  /** The customer holds a design that is no longer offered — they MUST choose a new one. */
  const currentIsDiscontinued = useMemo(
    () => designs.some((d) => d.code === effectiveCurrentStyle && d.discontinued),
    [designs, effectiveCurrentStyle]
  );

  // Pre-select the customer's current design when Core told us, else the
  // library default (or the first available design).
  const initialDesign = useMemo(() => {
    // Discontinued current design => select NOTHING. The FSR must pick a replacement
    // deliberately. Quietly falling back to the default here is what mailed a customer
    // the wrong card; an empty select that blocks submit is the honest failure.
    if (currentIsDiscontinued) return '';
    if (effectiveCurrentStyle && designs.some((d) => d.code === effectiveCurrentStyle)) return effectiveCurrentStyle;
    if (designs.some((d) => d.code === DEFAULT_DESIGN_CODE)) return DEFAULT_DESIGN_CODE;
    return designs[0]?.code ?? DEFAULT_DESIGN_CODE;
  }, [effectiveCurrentStyle, designs, currentIsDiscontinued]);

  const [reason, setReason] = useState<CardReissueReason>('Lost');
  const [mailTo, setMailTo] = useState<'Home' | 'Branch'>('Home');
  const [cardDesign, setCardDesign] = useState<string>(initialDesign);
  const [blockExisting, setBlockExisting] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const [successLast4, setSuccessLast4] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('Lost');
    setMailTo('Home');
    setCardDesign(initialDesign);
    setBlockExisting(true);
    setErr(null);
    setSuccessLast4(null);
  }, [open, initialDesign]);

  const reasonMeta = useMemo(
    () => REASONS.find((r) => r.code === reason) ?? REASONS[0],
    [reason],
  );

  // Auto-enforce block when reason forces it.
  useEffect(() => {
    if (reasonMeta.forcesBlock) setBlockExisting(true);
  }, [reasonMeta]);

  // A design must actually be chosen. This is only ever empty when the customer's
  // current design is discontinued and we deliberately refused to guess a replacement
  // for them (see initialDesign) — so the button stays disabled until the FSR picks one.
  // Without this the "silent swap" just moves from a bad default to an empty string.
  const canSave = !readOnly && !submitting && cardDesign !== '';

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSubmitting(true);
    setErr(null);
    const result = await run({
      cifId,
      acctId,
      acctType,
      reason,
      mailTo,
      cardDesign,
      blockExisting,
      officerCode,
      ...(existingCardNumber ? { existingCardNumber } : {}),
    });
    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error ?? 'Unknown error');
      return;
    }
    setSuccessLast4(result.newCardLast4 ?? null);
    onSaved?.();
    // Brief pause so the officer sees the new-last4 before closing.
    setTimeout(() => { onClose(); }, 1500);
  };

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={submitting ? () => { /* block */ } : onClose}
      title={`Re-issue Debit Card — ${acctId}${prodDesc ? ' (' + prodDesc + ')' : ''}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          {!readOnly && (
            <Button variant="primary" onClick={() => { void handleSave(); }} disabled={!canSave}>
              {submitting ? 'Issuing…' : 'Issue New Card'}
            </Button>
          )}
        </>
      }
    >
      {readOnly && (
        <Banner tone="warn" title="Read-only">
          You do not have the Card-Reissue grant.
        </Banner>
      )}

      {err && <Banner tone="err" title="Re-issue failed">{err}</Banner>}

      {successLast4 && (
        <Banner tone="ok" title="New card issued">
          Last-4: <code style={{ fontFamily: 'var(--font-mono)' }}>{successLast4}</code>
          {mailTo === 'Home' && ' — expect delivery in 7-10 business days.'}
        </Banner>
      )}

      <div>
        <label style={labelStyle}>Reason *</label>
        <select
          value={reason}
          onChange={(e) => { setReason(e.target.value as CardReissueReason); }}
          disabled={readOnly || submitting}
          style={inputStyle}
        >
          {REASONS.map((r) => (
            <option key={r.code} value={r.code}>{r.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={labelStyle}>Mail To *</label>
        <select
          value={mailTo}
          onChange={(e) => { setMailTo(e.target.value as 'Home' | 'Branch'); }}
          disabled={readOnly || submitting}
          style={inputStyle}
        >
          <option value="Home">Customer home address</option>
          <option value="Branch">Hold at branch (customer pickup)</option>
        </select>
      </div>

      <div>
        <label style={labelStyle}>Card Design</label>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          {(() => {
            // Show the CURRENT design when nothing is selected yet (the discontinued
            // case), so the FSR can see what the customer holds while choosing its
            // replacement.
            const sel = designs.find((d) => d.code === (cardDesign || effectiveCurrentStyle));
            if (sel?.imageUrl) {
              return (
                <img
                  src={sel.imageUrl}
                  alt={`${sel.label} card design`}
                  style={{ width: 76, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--ink-100)', flex: '0 0 auto' }}
                />
              );
            }
            if (sel?.discontinued) {
              // Discontinued placeholder. The original 102 black-card art is lost
              // (Bill 2026-07-16), so this stands in for it — deliberately flat and
              // labelled rather than a plausible-looking fake, because a made-up image
              // in a design picker is indistinguishable from the real thing, which is
              // the exact class of bug seed 140 just cleaned up.
              return (
                <div
                  title={`${sel.label} — discontinued; artwork not available`}
                  style={{
                    width: 76, height: 48, borderRadius: 6, flex: '0 0 auto',
                    background: 'repeating-linear-gradient(45deg, #2b2b2b, #2b2b2b 5px, #3a3a3a 5px, #3a3a3a 10px)', // theme-exempt: card-art placeholder hatch
                    border: '1px solid var(--input-border)', color: 'var(--ink-400)', fontSize: 8,
                    fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                  }}
                >
                  Discontinued
                </div>
              );
            }
            return (
              <div style={{ width: 76, height: 48, borderRadius: 6, border: '1px dashed var(--ink-100)', flex: '0 0 auto' }} aria-hidden />
            );
          })()}
          <select
            value={cardDesign}
            onChange={(e) => { setCardDesign(e.target.value); }}
            disabled={readOnly || submitting}
            style={inputStyle}
          >
            {currentIsDiscontinued && <option value="">— Choose a replacement design —</option>}
            {designs.map((d) => (
              <option key={d.code} value={d.code} disabled={d.discontinued}>
                {d.label}
                {d.code === effectiveCurrentStyle ? ' — current' : ''}
                {d.discontinued ? ' (Discontinued — no longer offered)' : ''}
              </option>
            ))}
          </select>
        </div>
        {currentIsDiscontinued ? (
          <div
            role="alert"
            style={{
              fontSize: 12, marginTop: 6, padding: '8px 10px', borderRadius: 6,
              background: 'rgba(177,5,5,0.08)', color: 'var(--pcb-red)',
              border: '1px solid rgba(177,5,5,0.25)',
            }}
          >
            This customer&apos;s current design (
            {designs.find((d) => d.code === effectiveCurrentStyle)?.label ?? effectiveCurrentStyle}
            ) is <strong>discontinued</strong> and can&apos;t be reprinted. Choose a
            replacement design and tell the customer their card will look different.
          </div>
        ) : effectiveCurrentStyle ? (
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4 }}>
            Pre-selected the customer&apos;s current design; change it only if they asked.
          </div>
        ) : null}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', fontSize: 13 }}>
        <input
          type="checkbox"
          checked={blockExisting}
          onChange={(e) => { setBlockExisting(e.target.checked); }}
          disabled={readOnly || submitting || reasonMeta.forcesBlock}
        />
        Block the existing card
        {reasonMeta.forcesBlock && (
          <span style={{ color: 'var(--pcb-red)', fontSize: 12 }}>
            (required for {reasonMeta.label})
          </span>
        )}
      </label>

      {existingCardNumber && blockExisting && (
        <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          Blocking: <code style={{ fontFamily: 'var(--font-mono)' }}>{existingCardNumber.slice(-4).padStart(existingCardNumber.length, '*')}</code>
        </div>
      )}

      {/* TODO: wire disclosure packet when Team 2's engine lands
          (temp-card letter for Lost/Stolen, name-change attestation). */}
    </ModalShell>
  );
}
